/**
 * wavetable — utilities for capturing a slice of audio off any
 * AudioNode and turning it into a single-cycle wavetable for
 * playback through an `AudioBufferSourceNode`.
 *
 * The capture flow:
 *
 *   1. Build a transient `AnalyserNode`, connect the source into it.
 *   2. Wait one short tick so the analyser fills its buffer with
 *      the most recent samples (some browsers won't have any data
 *      until at least one render quantum has elapsed).
 *   3. `getFloatTimeDomainData` into a fixed-length window.
 *   4. Disconnect the analyser and hand the samples back.
 *
 * That window IS the wavetable. We don't try to find the
 * fundamental period or align to zero crossings — a fixed-length
 * loop just plays whatever was captured and the user adjusts
 * Detune to "find" an in-tune position. Wavetable / sample-based
 * synths have done this for forty years and it works.
 *
 * Reference frequency for pitch tracking:
 *
 *   referenceHz = sampleRate / WAVETABLE_LENGTH
 *
 * The captured buffer represents one cycle at that frequency. To
 * play it back at MIDI note `n`:
 *
 *   playbackRate = midiToHz(n) / referenceHz
 */

/** Samples per captured wavetable. 2048 is a sweet spot:
 *  - Long enough to read as a "tone shape", not just a click.
 *  - Short enough that `playbackRate` stays in a reasonable range
 *    across the full keyboard. At 48 kHz this is a 23.4 Hz
 *    reference, so A4 (440 Hz) plays at ~18.8× — well within
 *    Web Audio's playback-rate window. */
export const WAVETABLE_LENGTH = 2048;

/**
 * Capture `WAVETABLE_LENGTH` samples from `sourceNode`'s output
 * into a fresh `Float32Array`. The returned promise resolves
 * after one render quantum so the analyser actually has data.
 *
 * The analyser is built locally and disconnected before resolving
 * — no audio-graph footprint persists past the call.
 */
export async function captureWavetable(
  ctx: AudioContext,
  sourceNode: AudioNode,
): Promise<Float32Array> {
  const ctxAny = ctx as unknown as {
    createAnalyser?: () => AnalyserNode;
  };
  if (typeof ctxAny.createAnalyser !== 'function') {
    // Fallback for shims without analyser support — return silence.
    return new Float32Array(WAVETABLE_LENGTH);
  }
  const analyser = ctx.createAnalyser();
  analyser.fftSize = WAVETABLE_LENGTH;
  let connected = false;
  try {
    sourceNode.connect(analyser);
    connected = true;
  } catch {
    // Already connected — proceed anyway, the analyser still
    // captures the connection that succeeded.
  }
  // Wait for at least one frame so the analyser fills its buffer.
  // 30 ms is comfortably more than one render quantum (≈3 ms at
  // 48 kHz / 128 samples) and stays well below "feels laggy" UI.
  await new Promise((r) => setTimeout(r, 30));

  const out = new Float32Array(WAVETABLE_LENGTH);
  analyser.getFloatTimeDomainData(
    out as unknown as Float32Array<ArrayBuffer>,
  );
  // Critical: disconnect the source → analyser edge, not just the
  // analyser's outgoing side (which has no destinations anyway).
  // Without this, every capture leaves a dangling AnalyserNode
  // hanging off the source, accumulating connections that survive
  // until the source is itself disposed. In particular, when
  // capturing from a wavefolder's output and then toggling its
  // bypass, the dangling analysers can interact with the chain
  // rewiring and silence the synth entirely.
  if (connected) {
    try {
      sourceNode.disconnect(analyser);
    } catch {
      /* already disconnected */
    }
  }
  try {
    analyser.disconnect();
  } catch {
    /* analyser had no outgoing — fine */
  }
  // Defensive — strip any DC offset so a captured wave with a
  // baseline (e.g. a ringing reverb tail) doesn't introduce a
  // constant rumble when it loops. Pure tone shapes from the
  // oscillator have no offset, so this is usually a no-op.
  let mean = 0;
  for (const v of out) mean += v;
  mean /= out.length;
  if (Math.abs(mean) > 1e-6) {
    for (let i = 0; i < out.length; i++) {
      const cur = out[i] ?? 0;
      out[i] = cur - mean;
    }
  }
  return out;
}

/**
 * Reference frequency the captured wavetable plays at when
 * `playbackRate = 1`. Equals `sampleRate / WAVETABLE_LENGTH`.
 */
export function wavetableReferenceHz(sampleRate: number): number {
  return sampleRate / WAVETABLE_LENGTH;
}

/**
 * Build an `AudioBuffer` from a captured wavetable. Stereo by
 * default — both channels share the mono capture so the buffer
 * source plays with the same shape on both sides; downstream
 * nodes can pan / detune as they wish.
 */
export function makeWavetableBuffer(
  ctx: BaseAudioContext,
  data: Float32Array,
): AudioBuffer {
  const buf = ctx.createBuffer(2, data.length, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const channel = buf.getChannelData(ch);
    channel.set(data);
  }
  return buf;
}
